import { useQuery, useQueryClient } from "@tanstack/react-query";
import { mxDaysFromToday } from "../mxDate";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import type { ThermoParts } from "../thermo/thermo";

/** Fila de la vista deal_board (una oportunidad abierta o cerrada, no archivada). */
export type DealRow = {
  id: number;
  key: string | null;
  name: string;
  offer_id: number;
  stage: string;
  amount: number;
  next_step_at: string | null;
  lost_reason: string | null;
  company_id: number;
  company_name: string;
  city: string | null;
  contact_ids: number[] | null;
  sales_first_name: string | null;
  sales_last_name: string | null;
  prob: number; // base por etapa × (0.6 + 0.8 × termómetro/100), tope 0.95
  idle_days: number | null;
  cad_done: number;
  cad_total: number;
  thermo: number | null;
  thermo_parts: ThermoParts | null;
  thermo_decay: number | null;
  offer_priority: number | null;
  trend_weeks: (number | null)[] | null;
  trend_2w: number | null;
  sector: string | null;
  phone: string | null;
  dnc: boolean;
};

// Valores permitidos por deals.lost_reason (09_raftel_ia.sql)
export const LOSS_REASONS = [
  ["precio", "Precio"],
  ["no_es_momento", "No es buen momento"],
  ["ya_tiene_proveedor", "Ya tiene proveedor"],
  ["no_le_duele", "No le duele"],
  ["no_decide", "No es quien decide"],
  ["sin_respuesta", "No contesta tras la cadencia"],
  ["otro", "Otro"],
] as const;

export const money = (n: number) =>
  n
    ? n.toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 0,
      })
    : "—";

export const lastContact = (days: number | null) =>
  days == null
    ? "—"
    : days === 0
      ? "hoy"
      : days === 1
        ? "ayer"
        : `hace ${days} días`;

export const useBoard = (offerId?: number) =>
  useQuery({
    queryKey: ["deal_board", offerId],
    enabled: !!offerId,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("deal_board")
        .select("*")
        .eq("offer_id", offerId!)
        .order("index", { ascending: true })
        .order("id", { ascending: true });
      if (error) throw error;
      return data as DealRow[];
    },
  });

/** Guarda cambios de una oportunidad y refresca tablero, menú y Mi día. */
export const useSaveDeal = () => {
  const queryClient = useQueryClient();
  const refresh = () =>
    [
      "deal_board",
      "funnel_by_offer",
      "my_day",
      "deals",
      "thermo_alerts",
    ].forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
  const save = async (id: number, data: Record<string, unknown>) => {
    const { error } = await getSupabaseClient()
      .from("deals")
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) throw error;
    refresh();
  };
  return { save, refresh };
};

export const in90days = () => mxDaysFromToday(90); // aaaa-mm-dd, México
